import React from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { useAuth } from "@/components/hooks/useAuth";

export const OAUTH_SIGN_IN = gql`
  mutation OAuthSignIn($provider: String!, $accessToken: String!) {
    oauthSignIn(accessToken: $accessToken, provider: $provider) {
      token
      user {
        id
        firstName
        lastName
        email
      }
      success
      mfaRequired
    }
  }
`;

interface GoogleOAuthButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export default function GoogleOAuthButton({
  onSuccess,
  onError,
  className = "",
  children,
}: GoogleOAuthButtonProps) {
  const { login } = useAuth();
  const [oauthSignIn, { loading }] = useMutation(OAUTH_SIGN_IN);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const result = await oauthSignIn({
          variables: {
            provider: "google",
            accessToken: response.access_token,
          },
        });

        if (result.data?.oauthSignIn?.success) {
          // Delegate token storage and post-auth navigation to central auth hook
          await login(result.data.oauthSignIn.token);

          // Call success callback if provided
          onSuccess?.();
        } else {
          throw new Error("OAuth authentication failed");
        }
      } catch (error) {
        console.error("Google OAuth error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Google sign-in failed";
        onError?.(errorMessage);
      }
    },
    onError: (error) => {
      console.error("Google OAuth error:", error);
      onError?.("Google sign-in failed");
    },
  });

  return (
    <button
      onClick={() => handleGoogleLogin()}
      disabled={loading}
      className={`flex items-center justify-center gap-3 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
      ) : (
        <>
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {children || "Google"}
        </>
      )}
    </button>
  );
}
